/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */
/* eslint-disable no-underscore-dangle */
/* eslint-disable react/no-unstable-nested-components */
import { useState, useEffect, useRef } from 'react';
import { Grid, Tabs, Tab, Link, Typography, Fab } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { dashboardActions } from 'modules/dashboard/slice';
import {
  selectEmbedToken,
  selectVisuals,
  // selectLatestUpdatedHotelsList,
  selectReportType,
  selectIsSignedIn,
  selectHotelName,
  selectTargetDate,
  selectPmsDate,
  selectTrendBookingDate,
  selectTrendTargetDate,
  selectGroupPmsDates,
  selectEnableHotelGroups,
  selectHotelGroupDropdownList,
  selectNotifications,
  selectWorkbookDateType,
  selectHotelDropdownList,
  selectLastPartialBookingDate,
  selectTabList,
} from 'modules/dashboard/selectors';
import { usersActions } from 'modules/users/slice';
import { selectSummaryBookingDate } from 'modules/dashboard/components/tab-container/summary-tab/selectors';
import { MessageSquare } from 'react-feather';
import CustomChatView from 'modules/common/components/custom-chat-view';
import { selectAuthUser, selectOrganizationId } from 'modules/common/auth/selectors';
import PAGINATION from 'modules/common/constants/pagination-limits';
import ORDER_BY from 'modules/common/constants/order-by';
import { BOOKING_DATE, REPORT_TYPE, VISUAL_DATE_TYPES } from 'modules/dashboard/constants';
import { ENVIRONMENT } from 'config';
import { formatDate } from 'modules/dashboard/functions';
import { ResponseModal } from 'modules/common/components';
import useMainCommandFlow from 'modules/dashboard/hooks/use-main-command-flow';
import useCustomLanding from 'modules/dashboard/hooks/use-custom-landing';
import { YEAR_MONTH_DAY_PATTERN } from 'modules/common/constants/date-range';
import { faroSendTabSelection, faroSetView } from 'modules/common/utils/faro';
import SummaryWidget from 'modules/dashboard/components/tab-container/summary-tab/components/widget-pane';
import TrendWidget from 'modules/dashboard/components/tab-container/trend-tabs/components/widget-pane';
import RegionalMap from 'modules/dashboard/components/tab-container/regional-map-tab/components/widget-pane';
import DashboardHeader from 'modules/dashboard/components/dashboard-header';
import WebClick from 'modules/dashboard/components/tab-container/web-click-tab/components/widget-pane';
import ShareComponent from 'modules/dashboard/components/tab-container/share';
import { isEmpty } from 'modules/common/helpers/object';
import IPrefer from 'modules/dashboard/components/tab-container/i-prefer-tab/components/widget-pane';
import { selectRegionMapBookingDate } from 'modules/dashboard/components/tab-container/regional-map-tab/selectors';
import { max, parseISO, startOfDay } from 'date-fns';
import useFetchNotifications from 'modules/dashboard/hooks/uses-fetch-notifications';
import { toast } from 'react-toastify';
import TOAST_TYPES from 'modules/common/constants/toast-types';
import WorkbookReport from './workbook-report/components/widget-pane';
import PaceWidget from './pace-tabs/components/widget-pane';
import PaceTestWidget from './pace-test-tab/components/widget-pane';
import TrendTabularWidget from './trend-tabular-tabs/components/widget-pane';
import ExportWorkbookReport from './workbook-report/components/widget-pane/components/neg-con-iata';
import { selectPaceBookingDate } from './pace-tabs/selectors';
import { selectWorkbookBookingDate } from './workbook-report/selectors';
import { summaryActions } from './summary-tab/slice';
import { regionalMapActions } from './regional-map-tab/slice';
import { paceActions } from './pace-tabs/slice';
import { workbookActions } from './workbook-report/slice';
import AlertWidget from './alert-tab/components';
import SearchWidget from './search/components';
import { selectSearchBookingDate, selectSelectedScenario } from './search/selectors';
import { searchActions } from './search/slice';
import PbiTrendWidget from './pbi-trend-tabs/components/widget-pane';

/**
 * All tab containers with report visuals
 * @returns
 */
const TabContainer = () => {
  useFetchNotifications();
  //
  const dispatch = useDispatch();
  // States are obtained from redux store. Refer each selector function for details.
  const organizationId = useSelector(selectOrganizationId);
  const lastPartialBookingDate = useSelector(selectLastPartialBookingDate);
  const tokenDetails = useSelector(selectEmbedToken);
  const visualDetails = useSelector(selectVisuals);
  const hotelList = useSelector(selectHotelDropdownList);
  const reportType = useSelector(selectReportType);
  const isSignIn = useSelector(selectIsSignedIn);
  const selectedHotel = useSelector(selectHotelName);
  const summaryBookingDate = useSelector(selectSummaryBookingDate);
  const searchBookingDate = useSelector(selectSearchBookingDate);
  const trendBookingDate = useSelector(selectTrendBookingDate);
  const otherTargetDate = useSelector(selectTargetDate);
  const trendTargetDate = useSelector(selectTrendTargetDate);
  const pmsDate = useSelector(selectPmsDate);
  const groupPmsDates = useSelector(selectGroupPmsDates);
  const currentUser = useSelector(selectAuthUser);
  const enableHotelGroups = useSelector(selectEnableHotelGroups);
  const hotelGroupList = useSelector(selectHotelGroupDropdownList);
  const notificationList = useSelector(selectNotifications);
  const authUser = useSelector(selectAuthUser);
  const workbookDateType = useSelector(selectWorkbookDateType);
  const regionalMapBookingDate = useSelector(selectRegionMapBookingDate);
  const paceBookingDate = useSelector(selectPaceBookingDate);
  const workbookBookingDate = useSelector(selectWorkbookBookingDate);
  const selectedScenario = useSelector(selectSelectedScenario);
  const TAB_VALUE = useSelector(selectTabList);
  //
  const accessToken = tokenDetails?.accessToken;
  // Local states to maintain visual objects and tab active state are defined here
  const [tabValue, setTabValue] = useState(0);
  const [regionMapVisual, setRegionMapVisual] = useState([]);
  const [webClickVisuals, setWebClickVisuals] = useState([]);
  const [webClickWeeklyVisuals, setWebClickWeeklyVisuals] = useState([]);
  const [webClickCumulativeVisuals, setWebClickCumulativeVisuals] = useState([]);
  const [iPreferVisuals, setIPreferVisuals] = useState([]);
  const [activeSummary, setActiveSummary] = useState(true);
  const [activeTrendTime, setActiveTrendTime] = useState(false);
  const [activeMuiTrendTime, setActiveMuiTrendTime] = useState(false);
  // const [activePlotly, setActivePlotly] = useState(false);
  const [activeRegionMap, setActiveRegionMap] = useState(false);
  const [activeWebClick, setActiveWebClick] = useState(false);
  const [activeIPrefer, setActiveIPrefer] = useState(false);
  const [activePace, setActivePace] = useState(false);
  const [activePaceTest, setActivePaceTest] = useState(false);
  const [latestBookingDate, setLatestBookingDate] = useState(
    hotelList?.find((hotel) => hotel?.id === selectedHotel?.id)?.latestBookingDate
  );
  const [hotelTimezone, setHotelTimezone] = useState(
    hotelList?.find((hotel) => hotel?.id === selectedHotel?.id)?.timezone
  );
  const [isChatOpen, setChatOpen] = useState(false);
  const [toggleDisabled, setToggleDisabled] = useState(true);
  //
  const [workbookVisuals, setWorkBookReport] = useState([]);
  const [activeWorkBookReport, setActiveWorkBook] = useState(false);
  const [activeExportWorkBookReport, setActiveExportWorkBook] = useState(false);
  const [activeAlert, setActiveAlert] = useState(false);
  const [activeSearch, setActiveSearch] = useState(false);
  //
  const toastId = useRef(null);
  const previousTabRef = useRef(null);
  const tabTriggerRef = useRef('programmatic');
  //
  useCustomLanding(setTabValue);
  // Set active report type
  const setSelectedReport = (type) => {
    dispatch(dashboardActions.setReportType(type));
  };
  //
  const TAB_CONFIG = [
    {
      key: 'SUMMARY',
      label: 'Summary',
      id: 'auto-tb-summary',
      short: 'sm',
      encoded: 'summary',
      className: 'summary-tab',
      enabled: true,
    },
    {
      key: 'WORKBOOK_REPORT',
      label: 'Dashboard',
      id: 'auto-tb-dashboard',
      short: 'wb',
      encoded: 'workbook',
      className: 'dashboard-tab',
      enabled: true,
    },
    {
      key: 'TREND_TIME',
      label: 'Trend',
      id: 'auto-tb-trend',
      short: 'td',
      encoded: 'trend',
      className: 'trend-tab',
      enabled: true,
    },
    {
      key: 'PACE',
      label: 'Pace',
      id: 'auto-tb-pace',
      short: 'pa',
      encoded: 'pace',
      className: 'pace-tab',
      enabled: true,
    },
    {
      key: 'PACE_TEST',
      label: 'Pace (Test)',
      id: 'auto-tb-pace-test',
      short: 'pat',
      encoded: 'pace-test',
      className: 'pace-test-tab',
      enabled: ENVIRONMENT.ENABLE_PBI_TEST_TAB === 'true',
    },
    {
      key: 'REGION_MAP',
      label: 'Regional Map',
      id: 'auto-tb-reginal-map',
      short: 'rm',
      encoded: 'regional-map',
      className: 'regional-map-tab',
      enabled: true,
    },
    {
      key: 'ALERT',
      label: 'Analytics',
      id: 'auto-mui-tb-alert',
      enabled: true,
      disabled: enableHotelGroups,
    },
    {
      key: 'MUI_TREND_TIME',
      label: 'Trend Tabular',
      id: 'auto-mui-tb-trend',
      short: 'tt',
      encoded: 'trend-tabular',
      enabled: ENVIRONMENT.ENABLE_TREND_TABULAR_TAB === 'true',
    },
    {
      key: 'SEARCH',
      label: 'Search',
      id: 'auto-tb-search',
      enabled: ENVIRONMENT.ENABLE_SEARCH_TAB === 'true',
    },
    {
      key: 'PBI_TREND',
      label: 'PBI Trend (Test)',
      id: 'auto-tb-pbi-trend',
      short: 'ptd',
      encoded: 'pbi-trend',
      enabled: ENVIRONMENT.ENABLE_PBI_TEST_TAB === 'true',
    },
    {
      key: 'EXPORT_WORKBOOK_REPORT',
      label: 'Exports',
      id: 'auto-mui-tb-export',
      short: 'ex',
      encoded: 'export',
      enabled: true,
    },
  ];
  const TABS = TAB_CONFIG.filter((tab) => tab.enabled);
  //
  useEffect(() => {
    const tabList = {};
    const shareUrlTabs = {};
    const encodedTabs = {};
    //
    TABS.forEach((tab, index) => {
      tabList[tab.key] = index;

      if (tab.short) {
        shareUrlTabs[tab.short] = index;
      }

      if (tab.encoded) {
        encodedTabs[index] = tab.encoded;
      }
    });
    //
    dispatch(dashboardActions.setTabList(tabList));
    dispatch(dashboardActions.setShareUrlTabList(shareUrlTabs));
    dispatch(dashboardActions.setEncodedTabList(encodedTabs));
  }, []);
  //
  const ToastComponent = ({ url }) => (
    <Grid>
      <Typography>Your Workbook PDF is ready</Typography>
      <Link
        id="auto-notification-card"
        href={`${ENVIRONMENT.AZURE_CDN_URL}/${url}`}
        // closing the toast marks the notification as opened (see onClose below)
        onClick={() => toast.dismiss(toastId.current)}
        target="_blank"
        sx={{ mx: 'auto' }}
      >
        <Typography>Download a PDF here</Typography>
      </Link>
    </Grid>
  );

  //
  useEffect(() => {
    if (notificationList?.length) {
      const filteredNotifications = notificationList?.filter(
        (notificationItem) => !notificationItem.isOpen
      );
      if (filteredNotifications?.length && !toast.isActive(toastId.current))
        toastId.current = toast(
          <ToastComponent url={filteredNotifications[0]?.payload.fileName} />,
          {
            type: TOAST_TYPES.INFO,
            autoClose: false,
            // dismissing the toast counts as acknowledging the PDF, so mark it as opened
            onClose: () =>
              dispatch(
                dashboardActions.updateNotification({
                  organizationId,
                  userId: authUser?.id,
                  notificationId: filteredNotifications[0]?.id,
                  isOpen: true,
                })
              ),
          }
        );
    }
  }, [notificationList]);

  // Triggered when organizationId value changes
  useEffect(() => {
    // Dispatch to get embed token, visuals, visual filters, system information and updated hotel list

    // prevent dispatches on browser refresh
    const serializedState = JSON.parse(localStorage.getItem(`persist:${ENVIRONMENT.PERSIST_KEY}`));
    if (!isSignIn && serializedState) {
      return;
    }
    let query = `limit=${PAGINATION.VISUALS_PER_PAGE_COUNT}&sortBy=order:${ORDER_BY.ASCENDING}`;
    dispatch(
      dashboardActions.getEmbedToken({
        organizationId,
      })
    );
    dispatch(
      dashboardActions.getVisuals({
        organizationId,
        query,
      })
    );
    query = `limit=${PAGINATION.VISUAL_FILTERS_PER_PAGE}&sortBy=type:${ORDER_BY.ASCENDING}`;
    dispatch(
      dashboardActions.getVisualFilters({
        organizationId,
        query,
      })
    );
    query = `limit=${PAGINATION.SYSTEM_INFORMATION}`;
    dispatch(
      dashboardActions.getSystemInformation({
        query,
      })
    );
    dispatch(dashboardActions.setIsSignedIn(false));
    // query = `limit=${PAGINATION.HOTELS_PER_PAGE_COUNT}&sortBy=latestBookingDate:${ORDER_BY.DESCENDING}`;
    // dispatch(
    //   dashboardActions.getLatestUpdatedHotelList({
    //     organizationId,
    //     query,
    //   })
    // );
  }, [organizationId]);
  //
  useEffect(() => {
    if (authUser && organizationId) {
      dispatch(usersActions.getStayDatesPresets({ organizationId, userId: authUser?.id }));
    }
  }, [organizationId, authUser]);

  // Triggered when organization and user value changes
  useEffect(() => {
    // get saved command filters
    const query = `limit=${PAGINATION.AGGREGATION_FILTER_PER_PAGE}&page=0`;
    dispatch(dashboardActions.getCommandFilter({ organizationId, userId: currentUser?.id, query }));
  }, [organizationId, currentUser]);
  // get landing page
  useEffect(() => {
    if (currentUser) {
      dispatch(usersActions.getLandingPage({ organizationId, userId: currentUser?.id }));
    }
  }, []);
  // Triggered when visualDetails value changes
  useEffect(() => {
    // Set visuals list based on report type
    setRegionMapVisual(
      visualDetails?.filter((visual) => visual?.tags?.includes(REPORT_TYPE.REGION_MAP))
    );
    // =============================
    setWebClickVisuals(
      visualDetails?.filter((visual) => visual?.tags?.includes(REPORT_TYPE.WEB_CLICK))
    );
    setWebClickWeeklyVisuals(
      visualDetails?.filter((visual) => visual?.tags?.includes(REPORT_TYPE.WEEKLY_WEB_CLICK))
    );
    setWebClickCumulativeVisuals(
      visualDetails?.filter((visual) => visual?.tags?.includes(REPORT_TYPE.CUMULATIVE_WEB_CLICK))
    );
    // =============================
    setIPreferVisuals(
      visualDetails?.filter((visual) => visual?.tags?.includes(REPORT_TYPE.I_PREFER))
    );
    // =============================
    setWorkBookReport(
      visualDetails?.filter((visual) => visual?.tags?.includes(REPORT_TYPE.WORKBOOK_REPORT))
    );
  }, [visualDetails]);

  // Triggered when tabValue changes
  useEffect(() => {
    // Set selected report based on tab value
    switch (tabValue) {
      case TAB_VALUE.SUMMARY:
        setActiveSummary(true);
        // setActivePlotly(false);
        setSelectedReport(REPORT_TYPE.SUMMARY);
        break;
      case TAB_VALUE.REGION_MAP:
        setActiveRegionMap(true);
        // setActivePlotly(false);
        setSelectedReport(REPORT_TYPE.REGION_MAP);
        break;
      case TAB_VALUE.TREND_TIME:
      case TAB_VALUE.PBI_TREND:
        setActiveTrendTime(true);
        // setActivePlotly(false);
        setSelectedReport(REPORT_TYPE.TREND_TIME);
        break;
      case TAB_VALUE.MUI_TREND_TIME:
        setActiveMuiTrendTime(true);
        // setActivePlotly(false);
        setSelectedReport(REPORT_TYPE.MUI_TREND_TIME);
        break;
      case TAB_VALUE.WEB_CLICK:
        setActiveWebClick(true);
        // setActivePlotly(false);
        setSelectedReport(REPORT_TYPE.WEB_CLICK);
        break;
      case TAB_VALUE.I_PREFER:
        setActiveIPrefer(true);
        // setActivePlotly(false);
        setSelectedReport(REPORT_TYPE.I_PREFER);
        break;
      case TAB_VALUE.WORKBOOK_REPORT:
        setActiveWorkBook(true);
        // setActivePlotly(false);
        setSelectedReport(REPORT_TYPE.WORKBOOK_REPORT);
        break;
      case TAB_VALUE.PACE:
        setActivePace(true);
        setSelectedReport(REPORT_TYPE.PACE);
        break;
      case TAB_VALUE.PACE_TEST:
        setActivePaceTest(true);
        setSelectedReport(REPORT_TYPE.PACE);
        break;
      case TAB_VALUE.EXPORT_WORKBOOK_REPORT:
        setActiveExportWorkBook(true);
        break;
      case TAB_VALUE.ALERT:
        setActiveAlert(true);
        break;
      case TAB_VALUE.SEARCH:
        setSelectedReport(REPORT_TYPE.SEARCH);
        setActiveSearch(true);
        break;
      default:
        break;
    }
  }, [tabValue]);

  // Send the tab selection to faro and keep the current tab as the faro view
  useEffect(() => {
    const tab = TABS[tabValue];
    faroSetView(tab?.label);
    if (previousTabRef.current !== null) {
      faroSendTabSelection({
        tab,
        previousTab: TABS[previousTabRef.current],
        hotel: selectedHotel,
        isHotelGroup: enableHotelGroups,
        trigger: tabTriggerRef.current,
      });
    }
    previousTabRef.current = tabValue;
    tabTriggerRef.current = 'programmatic';
  }, [tabValue]);
  // Clear the faro view when leaving the dashboard
  useEffect(() => () => faroSetView(undefined), []);

  // Triggered when selectedHotel, hotelList value changes
  useEffect(() => {
    // Get breakdown data, set latest booking date and timezone of selected hotel
    if (selectedHotel && hotelList && hotelGroupList) {
      let query = '';
      if (enableHotelGroups) {
        const hotelIds = selectedHotel?.hotels?.map((hotel) => `hotelIds=${hotel?.id}`);
        query = `${hotelIds?.join('&')}&limit=${PAGINATION.BREAKDOWN_DATA}`;
        //
        const earliestBookedHotel = selectedHotel?.hotels?.reduce((earliest, hotel) => {
          const selectedLatestBookingDate = hotel?.latestBookingDate;
          if (
            !earliest ||
            (selectedLatestBookingDate && selectedLatestBookingDate < earliest.latestBookingDate)
          ) {
            return hotel;
          }
          return earliest;
        }, null);
        setLatestBookingDate(earliestBookedHotel?.latestBookingDate);
        setHotelTimezone(earliestBookedHotel?.timezone);
      } else {
        query = `hotelId=${selectedHotel?.id}&limit=${PAGINATION.BREAKDOWN_DATA}`;
        setLatestBookingDate(
          hotelList?.find((hotel) => hotel?.id === selectedHotel?.id)?.latestBookingDate
        );
        setHotelTimezone(hotelList?.find((hotel) => hotel?.id === selectedHotel?.id)?.timezone);
      }
      //
      if (selectedHotel?.id || !isEmpty(selectedHotel?.hotels?.map((hotel) => hotel?.id))) {
        dispatch(dashboardActions.getBreakdown({ organizationId, query }));
        dispatch(dashboardActions.getUtmBreakdown({ organizationId, query }));
      }
    }
  }, [selectedHotel, hotelList, enableHotelGroups, hotelGroupList]);

  // Triggered when pmsDate, bookingDate, targetDate, selectedHotel values change
  useEffect(() => {
    const allBookingDatesAvailable =
      summaryBookingDate &&
      regionalMapBookingDate &&
      trendBookingDate &&
      paceBookingDate &&
      workbookBookingDate &&
      searchBookingDate;
    //
    if (!allBookingDatesAvailable) {
      return;
    }
    let bookingDate = {};
    let targetDate = {};
    switch (tabValue) {
      case TAB_VALUE.SUMMARY:
      case TAB_VALUE.WORKBOOK_REPORT:
      case TAB_VALUE.EXPORT_WORKBOOK_REPORT:
      case TAB_VALUE.ALERT:
      case TAB_VALUE.SEARCH:
        bookingDate = searchBookingDate;
        targetDate = otherTargetDate;
        break;
      case TAB_VALUE.REGION_MAP:
        bookingDate = regionalMapBookingDate;
        targetDate = otherTargetDate;
        break;
      case TAB_VALUE.PACE:
      case TAB_VALUE.PACE_TEST:
        targetDate = otherTargetDate;
        break;
      case TAB_VALUE.TREND_TIME:
      case TAB_VALUE.PBI_TREND:
      case TAB_VALUE.MUI_TREND_TIME:
        targetDate = trendTargetDate;
        bookingDate = trendBookingDate;
        break;
      default:
        break;
    }
    // Set pms toggle state based on hotel, booking date and target date
    if (enableHotelGroups) {
      if (groupPmsDates !== null && groupPmsDates?.length > 0) {
        const conditions = [
          (date) =>
            date &&
            (tabValue === TAB_VALUE.PACE || tabValue === TAB_VALUE.PACE_TEST) &&
            formatDate(date, YEAR_MONTH_DAY_PATTERN) <=
              formatDate(targetDate?.endDate, YEAR_MONTH_DAY_PATTERN), // Check if the value is odd
          (date) =>
            date &&
            tabValue !== TAB_VALUE.PACE &&
            tabValue !== TAB_VALUE.PACE_TEST &&
            formatDate(date, YEAR_MONTH_DAY_PATTERN) <=
              formatDate(targetDate?.endDate, YEAR_MONTH_DAY_PATTERN) &&
            formatDate(date, YEAR_MONTH_DAY_PATTERN) <=
              formatDate(bookingDate?.endDate, YEAR_MONTH_DAY_PATTERN),
        ];
        const enableStatus = groupPmsDates.every((date) =>
          conditions.some((condition) => condition(date))
        );
        //
        const enablePmsTooltipState =
          parseISO(targetDate?.startDate) < max(groupPmsDates.map(parseISO));
        //
        if (enableStatus) {
          setToggleDisabled(false);

          dispatch(dashboardActions.setPmsTooltipIsDisplay(enablePmsTooltipState));
        } else {
          setToggleDisabled(true);
          dispatch(dashboardActions.setPmsSync(false));
          dispatch(dashboardActions.setPmsTooltipIsDisplay(false));
        }
      } else {
        setToggleDisabled(true);
        dispatch(dashboardActions.setPmsSync(false));
        dispatch(dashboardActions.setPmsTooltipIsDisplay(false));
      }
    } else if (pmsDate) {
      if (
        (tabValue === TAB_VALUE.PACE || tabValue === TAB_VALUE.PACE_TEST) &&
        formatDate(pmsDate, YEAR_MONTH_DAY_PATTERN) <=
          formatDate(targetDate?.endDate, YEAR_MONTH_DAY_PATTERN)
      ) {
        setToggleDisabled(false);
        dispatch(
          dashboardActions.setPmsTooltipIsDisplay(
            formatDate(pmsDate, YEAR_MONTH_DAY_PATTERN) >
              formatDate(targetDate?.startDate, YEAR_MONTH_DAY_PATTERN)
          )
        );
      } else if (
        tabValue !== TAB_VALUE.PACE &&
        tabValue !== TAB_VALUE.PACE_TEST &&
        formatDate(pmsDate, YEAR_MONTH_DAY_PATTERN) <=
          formatDate(targetDate.endDate, YEAR_MONTH_DAY_PATTERN) &&
        formatDate(pmsDate, YEAR_MONTH_DAY_PATTERN) <=
          formatDate(bookingDate?.endDate, YEAR_MONTH_DAY_PATTERN)
      ) {
        setToggleDisabled(false);
        dispatch(
          dashboardActions.setPmsTooltipIsDisplay(
            formatDate(pmsDate, YEAR_MONTH_DAY_PATTERN) >
              formatDate(targetDate.startDate, YEAR_MONTH_DAY_PATTERN)
          )
        );
      } else {
        setToggleDisabled(true);
        dispatch(dashboardActions.setPmsSync(false));
        dispatch(dashboardActions.setPmsTooltipIsDisplay(false));
      }
    } else {
      setToggleDisabled(true);
      dispatch(dashboardActions.setPmsSync(false));
      dispatch(dashboardActions.setPmsTooltipIsDisplay(false));
    }
  }, [
    pmsDate,
    groupPmsDates,
    otherTargetDate,
    trendTargetDate,
    selectedHotel,
    enableHotelGroups,
    tabValue,
    summaryBookingDate,
    regionalMapBookingDate,
    trendBookingDate,
    paceBookingDate,
    workbookBookingDate,
    searchBookingDate,
  ]);

  // Synchronizes the initial date range across all feature tabs.
  useEffect(() => {
    if (!lastPartialBookingDate) return;
    //
    const resetBookingDate = {
      startDate: new Date(BOOKING_DATE).toISOString(),
      endDate: startOfDay(parseISO(lastPartialBookingDate)).toISOString(),
      key: 'selection',
    };
    dispatch(dashboardActions.setResetBookingDate(resetBookingDate));
    //
    const tabs = [
      { value: summaryBookingDate, action: summaryActions.setSummaryBookingDate },
      { value: regionalMapBookingDate, action: regionalMapActions.setRegionMapBookingDate },
      { value: trendBookingDate, action: dashboardActions.setTrendBookingDate },
      { value: paceBookingDate, action: paceActions.setPaceBookingDate },
      { value: workbookBookingDate, action: workbookActions.setWorkbookBookingDate },
      { value: searchBookingDate, action: searchActions.setSearchBookingDate },
    ];
    //
    tabs.forEach(({ value, action }) => {
      if (value === null) {
        dispatch(action(resetBookingDate));
      }
    });
  }, [lastPartialBookingDate]);
  //
  useEffect(() => {
    if (enableHotelGroups && tabValue === TAB_VALUE.ALERT) {
      setTabValue(TAB_VALUE.SUMMARY);
    }
  }, [selectedHotel, hotelList, enableHotelGroups, hotelGroupList]);

  /**
   *  This is where add listener functions to all the ui controller commands to set values and emit listener functions in particular tab.
   * As for event driven flow, this is the first step, adding event listener for events
   * Then we emit these events command values
   * commands values are provided as parameters to listener functions
   */
  useMainCommandFlow(tabValue, setTabValue);
  // Analytics has no filter row beneath the tabs.
  const tabHeaderHeight = tabValue === TAB_VALUE.ALERT ? 64 : 100;
  //
  //
  return (
    <Grid container justifyContent="flex-start" alignItems="center" px={{ xs: 1, md: 4, sm: 2 }}>
      <Grid
        sx={{
          width: '100%',
          background: '#f7f7f7',
          top: 75,
          position: 'fixed',
          zIndex: 15,
          height: tabHeaderHeight,
        }}
      >
        <Grid
          container
          sx={{
            background: '#f7f7f7',
            flexDirection: 'row',
            justifyContent: 'space-between',
            ml: 10,
          }}
        >
          <Grid item xs={10}>
            <Tabs
              value={tabValue}
              onChange={(e, val) => {
                tabTriggerRef.current = 'click';
                setTabValue(val);
              }}
              variant="scrollable"
              scrollButtons="auto"
            >
              {TABS.map((tab) => (
                <Tab
                  key={tab.key}
                  label={tab.label}
                  id={tab.id}
                  className={tab.className}
                  disabled={tab.disabled}
                />
              ))}
            </Tabs>
          </Grid>
          <Grid item xs={2} alignSelf="center">
            <ShareComponent tabValue={tabValue} />
          </Grid>
        </Grid>
        <DashboardHeader
          reportType={reportType}
          adornment={
            workbookDateType?.id === VISUAL_DATE_TYPES.ARRIVAL &&
            tabValue === TAB_VALUE.WORKBOOK_REPORT
              ? 'Arrival Dates'
              : 'Stay Dates'
          }
          latestDate={latestBookingDate}
          isDisabled={
            tabValue === TAB_VALUE.EXPORT_WORKBOOK_REPORT ||
            tabValue === TAB_VALUE.ALERT ||
            (tabValue === TAB_VALUE.SEARCH && !selectedScenario)
          }
        />
      </Grid>
      <Grid sx={{ height: tabHeaderHeight, whiteSpace: 'pre-wrap' }}> </Grid>
      {summaryBookingDate && (
        <SummaryWidget
          value={tabValue}
          active={activeSummary}
          accessToken={accessToken}
          latestDate={latestBookingDate}
          hotelTimezone={hotelTimezone}
          reportType={reportType}
          toggleDisabled={toggleDisabled}
          setToggleDisabled={setToggleDisabled}
        />
      )}
      {workbookVisuals && workbookBookingDate && (
        <WorkbookReport
          value={tabValue}
          active={activeWorkBookReport}
          accessToken={accessToken}
          visuals={workbookVisuals}
          latestDate={latestBookingDate}
          hotelTimezone={hotelTimezone}
          reportType={reportType}
          toggleDisabled={toggleDisabled}
        />
      )}
      {workbookBookingDate && (
        <ExportWorkbookReport value={tabValue} active={activeExportWorkBookReport} />
      )}
      {summaryBookingDate && (
        <SearchWidget value={tabValue} active={activeSearch} latestDate={latestBookingDate} />
      )}
      <AlertWidget value={tabValue} active={activeAlert} />

      {regionMapVisual && regionalMapBookingDate && (
        <RegionalMap
          value={tabValue}
          active={activeRegionMap}
          accessToken={accessToken}
          visuals={regionMapVisual}
          latestDate={latestBookingDate}
          hotelTimezone={hotelTimezone}
          reportType={reportType}
          toggleDisabled={toggleDisabled}
        />
      )}
      {trendBookingDate && (
        <>
          <TrendWidget
            value={tabValue}
            active={activeTrendTime}
            latestDate={latestBookingDate}
            hotelTimezone={hotelTimezone}
            reportType={reportType}
            toggleDisabled={toggleDisabled}
          />
          <TrendTabularWidget
            value={tabValue}
            active={activeMuiTrendTime}
            latestDate={latestBookingDate}
            hotelTimezone={hotelTimezone}
            reportType={reportType}
            toggleDisabled={toggleDisabled}
          />
          <PbiTrendWidget
            value={tabValue}
            active={activeTrendTime}
            latestDate={latestBookingDate}
            hotelTimezone={hotelTimezone}
            reportType={reportType}
            toggleDisabled={toggleDisabled}
          />
        </>
      )}
      {webClickVisuals && (
        <WebClick
          value={tabValue}
          active={activeWebClick}
          accessToken={accessToken}
          visuals={webClickVisuals}
          weeklyVisuals={webClickWeeklyVisuals}
          cumulativeVisuals={webClickCumulativeVisuals}
          latestDate={latestBookingDate}
          hotelTimezone={hotelTimezone}
          reportType={reportType}
        />
      )}
      {iPreferVisuals && (
        <IPrefer
          value={tabValue}
          active={activeIPrefer}
          accessToken={accessToken}
          visuals={iPreferVisuals}
          latestDate={latestBookingDate}
          hotelTimezone={hotelTimezone}
          reportType={reportType}
          toggleDisabled={toggleDisabled}
          setToggleDisabled={setToggleDisabled}
        />
      )}
      {paceBookingDate && (
        <PaceWidget
          active={activePace}
          value={tabValue}
          reportType={reportType}
          hotelTimezone={hotelTimezone}
          latestDate={latestBookingDate}
          toggleDisabled={toggleDisabled}
        />
      )}
      {paceBookingDate && (
        <PaceTestWidget
          active={activePaceTest}
          value={tabValue}
          reportType={reportType}
          hotelTimezone={hotelTimezone}
          latestDate={latestBookingDate}
          toggleDisabled={toggleDisabled}
        />
      )}
      {/* TODO: Comment for 4.0.0.0 Release */}
      {ENVIRONMENT.CHAT_ENABLED === '1' && (
        <>
          <Fab
            color="secondary"
            sx={{ position: 'fixed', bottom: 2, right: 0 }}
            onClick={() => setChatOpen(true)}
          >
            <MessageSquare />
          </Fab>
          <CustomChatView
            isOpen={isChatOpen}
            setOpen={() => setChatOpen(false)}
            setTabValue={setTabValue}
            tabValue={tabValue}
          />
        </>
      )}
      {ENVIRONMENT.IS_VISUAL_INTERPRETER_ENABLED?.toLowerCase() === 'true' && (
        <ResponseModal
          handleClose={() => {
            dispatch(dashboardActions.setOpenVisualAgentModal(false));
            dispatch(dashboardActions.setIsFirstTimeInterpreterAgentUsed(false));
          }}
          title="Visual Interpreter Agent"
        />
      )}
    </Grid>
  );
};
//
export default TabContainer;
